/**
 * Variant grid (SPEC screen 2, tab 2): one editable row per product variant.
 * Attribute-value cells are generated from the product's ASSIGNED attribute
 * definitions (assignment position order); validation is the Zod mirror
 * (client convenience) + the server's `errors[]` mapped onto cells via paths
 * like `variants[2].attribute_values.color`.
 */
import { useTranslation } from "react-i18next";
import { useFieldArray, useFormContext, type FieldPath } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { FormControl, FormField, FormItem, FormMessage } from "@/components/form";

import { VARIANT_STATUSES, type Attribute } from "../api";
import type { EditorValues } from "../variant-schema";

export interface VariantGridProps {
  attributes: Attribute[];
  /** Barcode count per variant id (derived from the detail aggregate). */
  barcodeCounts: Record<string, number>;
  /** "Barcodes" cell → Barcodes tab filtered to this variant. */
  onOpenBarcodes: (variantId: string) => void;
  /** Deep-linked variant row (`?variant=`) gets highlighted. */
  highlightVariantId?: string;
  canEdit?: boolean;
  canReadBarcodes?: boolean;
}

export function VariantGrid({
  attributes,
  barcodeCounts,
  onOpenBarcodes,
  highlightVariantId,
  canEdit = true,
  canReadBarcodes = true,
}: VariantGridProps) {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<EditorValues>();
  const { fields, append, remove } = useFieldArray({ control, name: "variants" });

  /**
   * Replacement never deletes a child: the server upserts the submitted set and
   * leaves anything omitted exactly as it was. Splicing a persisted row out of
   * the form would therefore look like a deletion while the variant quietly
   * survived. Only an unsaved row can be dropped locally; a persisted one is
   * archived in place so the same Save carries the real intent.
   */
  const discardVariant = (index: number): void => {
    const variantId = getValues(`variants.${index}.variantId`);
    if (variantId === "") {
      remove(index);
      return;
    }
    setValue(`variants.${index}.status`, "archived", {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const statusLabel = (value: string): string => t(`catalog.list.status.${value}`, value);

  return (
    <div className="flex flex-col gap-3">
      <div className="w-full overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th scope="col" className="p-2 text-start font-medium">
                {t("catalog.editor.variants.sku", "SKU")}
              </th>
              {attributes.map((attribute) => (
                <th scope="col" key={attribute.id} className="p-2 text-start font-medium">
                  {/* Attribute codes are machine slugs — LTR. */}
                  <span dir="ltr">{attribute.code}</span>
                </th>
              ))}
              <th scope="col" className="p-2 text-start font-medium">
                {t("catalog.editor.variants.status", "Status")}
              </th>
              <th scope="col" className="p-2 text-start font-medium">
                {t("catalog.editor.variants.barcodes", "Barcodes")}
              </th>
              <th scope="col" className="p-2" />
            </tr>
          </thead>
          <tbody>
            {fields.length === 0 ? (
              <tr>
                <td
                  colSpan={attributes.length + 4}
                  className="p-6 text-center text-sm text-muted-foreground"
                >
                  {t("catalog.editor.variants.empty", "No variants yet — add the first one.")}
                </td>
              </tr>
            ) : (
              fields.map((row, index) => (
                <tr
                  key={row.id}
                  data-variant-id={row.variantId}
                  className={
                    row.variantId !== "" && row.variantId === highlightVariantId
                      ? "border-b bg-muted/40"
                      : "border-b"
                  }
                >
                  <td className="p-2 align-top">
                    <FormField
                      control={control}
                      name={`variants.${index}.sku`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            {/* SKUs are Latin codes — LTR inside RTL. */}
                            <Input
                              disabled={!canEdit}
                              dir="ltr"
                              className="w-40 font-mono"
                              aria-label={t("catalog.editor.variants.sku", "SKU")}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </td>
                  {attributes.map((attribute) => (
                    <td key={attribute.id} className="p-2 align-top">
                      <FormField
                        control={control}
                        name={
                          `variants.${index}.attribute_values.${attribute.code}` as FieldPath<EditorValues>
                        }
                        render={({ field }) => (
                          <FormItem>
                            {attribute.type === "select" ? (
                              /* key: remount on external value swap (reset) —
                                 Radix emits onValueChange("") otherwise. */
                              <Select
                                key={typeof field.value === "string" ? field.value : ""}
                                value={typeof field.value === "string" ? field.value : ""}
                                onValueChange={field.onChange}
                                disabled={!canEdit}
                              >
                                <FormControl>
                                  <SelectTrigger className="w-32" aria-label={attribute.code}>
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {attribute.options.map((option) => (
                                    <SelectItem key={option} value={option}>
                                      {option}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <FormControl>
                                <Input
                                  disabled={!canEdit}
                                  // Numbers/codes stay LTR digit runs.
                                  dir={attribute.type === "number" ? "ltr" : undefined}
                                  className="w-32"
                                  aria-label={attribute.code}
                                  value={typeof field.value === "string" ? field.value : ""}
                                  onChange={field.onChange}
                                  onBlur={field.onBlur}
                                  name={field.name}
                                  ref={field.ref}
                                />
                              </FormControl>
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </td>
                  ))}
                  <td className="p-2 align-top">
                    <FormField
                      control={control}
                      name={`variants.${index}.status`}
                      render={({ field }) => (
                        <FormItem>
                          <Select
                            key={field.value}
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={!canEdit}
                          >
                            <FormControl>
                              <SelectTrigger
                                className="w-36"
                                aria-label={t("catalog.editor.variants.status", "Status")}
                              >
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {VARIANT_STATUSES.map((status) => (
                                <SelectItem key={status} value={status}>
                                  {statusLabel(status)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </td>
                  <td className="p-2 align-top">
                    {canReadBarcodes ? (
                      row.variantId !== "" ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => onOpenBarcodes(row.variantId)}
                        >
                          <Badge variant="secondary">{barcodeCounts[row.variantId] ?? 0}</Badge>
                          {t("catalog.editor.variants.openBarcodes", "Open")}
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {t("catalog.editor.variants.saveFirst", "Save first")}
                        </span>
                      )
                    ) : null}
                  </td>
                  <td className="p-2 align-top">
                    {canEdit ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={
                          row.variantId === ""
                            ? t("catalog.editor.variants.remove", "Remove variant")
                            : t("catalog.editor.variants.archive", "Archive variant")
                        }
                        onClick={() => discardVariant(index)}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {canEdit ? (
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              append({ variantId: "", sku: "", status: "active", attribute_values: {} })
            }
          >
            <Plus className="size-4" aria-hidden="true" />
            {t("catalog.editor.variants.add", "Add variant")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
